import React, {useState, type PropsWithChildren, type ReactNode} from 'react';
import {
  IconButtonProps,
  HStack,
  IconButton,
  Tooltip,
  VStack,
  View,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';

const FloatMainContainer = emotionStyled(View)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  position: 'absolute',
  minHeight: '3.5rem',
  top: 0,
  bottom: '-$1',
  right: '$1',
});

interface MessageHoverActionsProps extends PropsWithChildren {
  renderActions: () => ReactNode;
}

/**
 * ## MessageHoverActions
 * Used to display additional actions for message boxes.
 * It should wrap `Message` component.
 *
 * @example
 * ```jsx
 *     <MessageHoverActions
 *       renderActions={() => (
 *         <>
 *           <MessageActionButton Icon={<DuplicateFillIcon />} label="Copy" />
 *         </>
 *       )}
 *     >
 *       <Message inputRole="user">
 *         <MessageContent>
 *           This is a message sent by user
 *         </MessageContent>
 *       </Message>
 *    </MessageHoverActions>
 * ```
 */
export const MessageHoverActions = ({
  children,
  renderActions,
}: MessageHoverActionsProps) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <VStack
      width="auto"
      position="relative"
      onMouseOver={() => {
        setIsHovering(true);
      }}
      onMouseOut={() => {
        setIsHovering(false);
      }}
    >
      {children}
      {isHovering && (
        <FloatMainContainer>
          <HStack
            data-id="action-button-group"
            height="22px"
            padding="$0.25"
            paddingX="$0.5"
            border="$border1"
            borderRadius="$2"
            width="fit-content"
            backgroundColor="$background2"
          >
            {renderActions?.()}
          </HStack>
        </FloatMainContainer>
      )}
    </VStack>
  );
};

/**
 * ## MessageActionButton
 * IconButton with integrated Tooltip
 */
export const MessageActionButton = (props: IconButtonProps) => {
  return (
    <Tooltip label={props.label} asChild>
      <IconButton density="compact" variant="ghost" {...props} />
    </Tooltip>
  );
};
